import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import logoImg from '../assets/blast-logo.webp';

try {
  delete L.Icon.Default.prototype._getIconUrl;
} catch (e) {
  // ignore kalau property non-configurable
}
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3301/api';

export default function LiveTouring() {
  const { id } = useParams();
  const [checkpoints, setCheckpoints] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [roadRoute, setRoadRoute] = useState(null);

  const fetchData = () => {
    Promise.all([
      axios.get(`${API_BASE}/live-touring/${id}/checkpoints`),
      axios.get(`${API_BASE}/live-touring/${id}/summary`),
    ])
      .then(([cpRes, sumRes]) => {
        setCheckpoints(cpRes.data);
        setSummary(sumRes.data);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));

    // Rute jalan asli (OSRM) - server yang nge-cache, jadi aman dipanggil tiap polling
    axios.get(`${API_BASE}/live-touring/${id}/route`)
      .then((res) => {
        // OSRM balikin [lon, lat], Leaflet butuh [lat, lon] - swap dulu
        const swapped = (res.data.coordinates || []).map((c) => [c[1], c[0]]);
        setRoadRoute(swapped.length > 1 ? swapped : null);
      })
      .catch((err) => {
        console.error('Fetch road route error:', err);
        setRoadRoute(null); // fallback ke garis lurus kalau gagal
      });
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const validPoints = checkpoints.filter((c) => c.latitude && c.longitude);
  const straightPositions = validPoints.map((c) => [parseFloat(c.latitude), parseFloat(c.longitude)]);
  // Pakai rute jalan asli kalau ada, fallback ke garis lurus antar checkpoint kalau OSRM gagal/belum cukup titik
  const positions = roadRoute && roadRoute.length > 1 ? roadRoute : straightPositions;
  const center = straightPositions.length > 0 ? straightPositions[0] : [-6.914744, 107.60981];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#12151A', color: '#8B92A0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Memuat peta live touring...
      </div>
    );
  }

  return (
    <div style={{ background: '#12151A', color: '#F0EDE4', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" }}>
      <nav style={{ padding: '18px 6vw', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <img src={logoImg} alt="Blast Motor Club" style={{ height: 36, width: 'auto' }} />
        <Link to="/" style={{ color: '#D02127', fontSize: 13, textDecoration: 'none' }}>← Beranda</Link>
      </nav>
      <div style={{ padding: '0 6vw 60px', maxWidth: 900, margin: '0 auto' }}>
        <span style={{ color: '#D02127', fontWeight: 800, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase' }}>
          🔴 Live Touring
        </span>
        <h1 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, fontStyle: 'italic', margin: '6px 0 24px' }}>
          Pantau Perjalanan
        </h1>
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14, marginBottom: 24 }}>
            <div style={{ background: '#1C2128', border: '1px solid #262b33', borderRadius: 8, padding: 18, textAlign: 'center' }}>
              <p style={{ fontSize: 24, fontWeight: 900, color: '#2B6996' }}>{summary.jumlah_checkpoint}</p>
              <p style={{ fontSize: 11, color: '#8B92A0', marginTop: 4 }}>Checkpoint</p>
            </div>
            <div style={{ background: '#1C2128', border: '1px solid #262b33', borderRadius: 8, padding: 18, textAlign: 'center' }}>
              <p style={{ fontSize: 24, fontWeight: 900, color: '#D02127' }}>{summary.jarak_km} km</p>
              <p style={{ fontSize: 11, color: '#8B92A0', marginTop: 4 }}>Jarak Tempuh</p>
            </div>
            <div style={{ background: '#1C2128', border: '1px solid #262b33', borderRadius: 8, padding: 18, textAlign: 'center' }}>
              <p style={{ fontSize: 24, fontWeight: 900, color: '#2B6996' }}>{summary.durasi_menit} min</p>
              <p style={{ fontSize: 11, color: '#8B92A0', marginTop: 4 }}>Durasi</p>
            </div>
          </div>
        )}
        {straightPositions.length > 0 ? (
          <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #262b33', marginBottom: 32, height: 400 }}>
            <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
              />
              <Polyline positions={positions} color="#D02127" weight={4} />
              {validPoints.map((c) => (
                <Marker key={c.id} position={[parseFloat(c.latitude), parseFloat(c.longitude)]}>
                  <Popup>
                    <b>Checkpoint {c.urutan}</b><br />
                    {c.keterangan || '-'}<br />
                    <small>oleh {c.posted_by_name}</small>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        ) : (
          <p style={{ color: '#8B92A0', marginBottom: 32 }}>Belum ada titik lokasi yang tercatat.</p>
        )}
        <h2 style={{ fontSize: 18, fontWeight: 800, fontStyle: 'italic', marginBottom: 16 }}>Riwayat Checkpoint</h2>
        {checkpoints.length === 0 ? (
          <p style={{ color: '#8B92A0', fontSize: 13 }}>Belum ada checkpoint.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {checkpoints.map((c) => (
              <div key={c.id} style={{ background: '#1C2128', border: '1px solid #262b33', borderRadius: 8, padding: 16 }}>
                <p style={{ color: '#D02127', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Checkpoint {c.urutan}</p>
                <p style={{ fontSize: 14, marginTop: 4 }}>{c.keterangan || '-'}</p>
                <p style={{ color: '#8B92A0', fontSize: 12, marginTop: 6 }}>
                  Oleh {c.posted_by_name} ({c.posted_by_position}) · {new Date(c.created_at).toLocaleString('id-ID')}
                </p>
                {c.photos && c.photos.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    {c.photos.map((p, i) => (
                      <img
                        key={i}
                        src={p}
                        alt=""
                        onClick={() => setPreviewPhoto(p)}
                        style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {previewPhoto && (
        <div
          onClick={() => setPreviewPhoto(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 100,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <img
            src={previewPhoto}
            alt=""
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 8, objectFit: 'contain' }}
          />
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
            <a
              href={`${API_BASE}/live-touring/checkpoint-photo/download?url=${encodeURIComponent(previewPhoto)}`}
              style={{ background: '#D02127', color: '#fff', padding: '10px 22px', borderRadius: 6, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}
            >
              ⬇ Unduh
            </a>
            <button
              onClick={() => setPreviewPhoto(null)}
              style={{ background: 'transparent', border: '1px solid #262b33', color: '#F0EDE4', padding: '10px 22px', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
            >
              Tutup
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
